import type { ColumnDef } from '@tanstack/react-table';
import { Route as RouteIcon } from 'lucide-react';
import {
  DataTable,
  FEW_ROWS_TABLE_FRAME_CLASS,
  StackedCellText,
} from '@/components/common/DataTable';
import { DetailPanel } from '@/components/common/DetailPanel';
import { EmptyValue } from '@/components/common/EmptyValue';
import { ExpandableComment } from '@/components/common/ExpandableComment';
import { StatusBadge } from '@/components/common/StatusBadge';
import { formatDisplayDate, formatTime } from '@/utils/date';

export interface DecisionStep {
  key: string;
  step: string;
  isDone: boolean;
  by: string | null;
  at: string | null;
  comment: string | null;
}

const DECISION_STEP_COLUMNS: ColumnDef<DecisionStep, unknown>[] = [
  {
    id: 'step',
    header: 'Step',
    cell: ({ row }) => (
      <span className="flex items-center gap-2 whitespace-nowrap">
        <span className="font-medium">{row.original.step}</span>
        <StatusBadge tone={row.original.isDone ? 'success' : 'warning'}>
          {row.original.isDone ? 'Done' : 'Pending'}
        </StatusBadge>
      </span>
    ),
  },
  {
    id: 'by',
    header: 'By',
    cell: ({ row }) => row.original.by ?? <EmptyValue />,
  },
  {
    id: 'at',
    header: 'When',
    cell: ({ row }) =>
      row.original.at ? (
        <StackedCellText
          primary={formatDisplayDate(row.original.at)}
          secondary={formatTime(row.original.at)}
        />
      ) : (
        <EmptyValue />
      ),
  },
  {
    id: 'comment',
    header: 'Comment',
    cell: ({ row }) =>
      row.original.comment ? (
        <ExpandableComment comment={row.original.comment} shouldKeepLineBreaks />
      ) : (
        <EmptyValue />
      ),
  },
];

interface DecisionTrailPanelProps {
  title: string;
  steps: DecisionStep[] | undefined;
  skeletonRowCount: number;
}

export function DecisionTrailPanel({ title, steps, skeletonRowCount }: DecisionTrailPanelProps) {
  return (
    <DetailPanel title={title} icon={RouteIcon}>
      <DataTable
        data={steps ?? []}
        columns={DECISION_STEP_COLUMNS}
        isLoading={steps === undefined}
        skeletonRowCount={skeletonRowCount}
        className={FEW_ROWS_TABLE_FRAME_CLASS}
      />
    </DetailPanel>
  );
}
